import React, { useState, useEffect, useCallback } from 'react';
import { Form, Upload, Button, message, Popover } from 'antd';
import { CheckCircleOutlined, InfoCircleOutlined, CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import imageCompression from 'browser-image-compression';
import api from '../api/axios';
import { useProfile } from '../context/ProfileContext';

const Verifications = () => {
    const [fields, setFields] = useState([]);

    const { profileId } = useProfile();

    const fetchVerifications = useCallback(async () => {
        try {
            const response = await api.get('/verifications/list', {
                params: { profile_id: profileId },
            });

            const verificationData = response.data.data;

            // Map the response data to match the field structure
            const updatedFields = [
                { label: 'Name & DOB (Aadhaar)', type: 'name_dob', status: verificationData.name_dob_status },
                { label: 'Salary & Workplace (Payslip)', type: 'salary_workplace', status: verificationData.workplace_salary_status },
                { label: 'Education (Degree Certificate)', type: 'education', status: verificationData.education_status },
                { label: 'Caste (Community Certificate)', type: 'caste', status: verificationData.caste_status },
                { label: 'Religion (Community Certificate / Religion Certificate)', type: 'religion', status: verificationData.religion_status },
                { label: 'House (Encumbrance Certificate / House Tax Receipt)', type: 'house', status: verificationData.house_status },
                { label: 'Car (Registration Certificate / RC Book)', type: 'car', status: verificationData.car_status },
            ];

            setFields(updatedFields);
        } catch (error) {
            console.error('Error fetching verification data', error);
            message.error('Failed to load verification statuses');
        }
    }, [profileId]);

    useEffect(() => {
        if (profileId) {
            fetchVerifications();
        } else {
            message.error('Profile ID is missing');
        }
    }, [profileId, fetchVerifications]);

    const handleFileUpload = async (file, fieldType) => {
        let uploadFile = file;

        // Compress only image files — PDFs are skipped
        if (file.type.startsWith('image/')) {
            uploadFile = await imageCompression(file, {
                maxSizeMB: 1,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
            });
        }

        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('profile_id', profileId);
        formData.append('type', fieldType);

        try {
            const response = await api.post('/verifications/add', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.status === 'success') {
                message.success('Verification added successfully');
                fetchVerifications();
            } else {
                message.error('Failed to upload verification');
                console.error(response.data.message);
            }
        } catch (error) {
            message.error('An error occurred while uploading the file');
            console.error(error);
        }

        return false;
    };

    const popoverContent = (
        <div>
            <p style={{ fontSize: '0.75rem', margin: 0 }}>
                Acceptable file types: .png, .jpg, .jpeg, .pdf.
            </p>
            <p style={{ fontSize: '0.75rem', margin: 0 }}>
                Maximum upload size: 2 MB.
            </p>
        </div>
    );

    const uploadButton = (
        <Popover content={popoverContent} title="File Upload Guidelines" trigger="hover">
            <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
            </div>
        </Popover>
    );

    return (
        <Form layout="vertical" style={{ fontFamily: 'Calibri, sans-serif' }}>
            <div style={{
                background: '#FFF8E1', border: '1px solid #FFD54F', borderRadius: 8,
                padding: '10px 14px', marginBottom: 20, fontSize: '0.8rem', color: '#795548', lineHeight: 1.6,
            }}>
                <strong>Accepted Documents:</strong><br />
                • <strong>Name &amp; DOB:</strong> Aadhaar Card<br />
                • <strong>Salary &amp; Workplace:</strong> Payslip (latest)<br />
                • <strong>Education:</strong> Degree Certificate<br />
                • <strong>Caste:</strong> Community Certificate<br />
                • <strong>Religion:</strong> Community Certificate / Religion Certificate<br />
                • <strong>House:</strong> Encumbrance Certificate / House Tax Receipt<br />
                • <strong>Car:</strong> Registration Certificate / RC Book<br />
                <span style={{ marginTop: 8, display: 'inline-block' }}>
                    ⚠️ All documents must have the <strong>same name as mentioned in your profile</strong>.
                </span>
            </div>
            {fields.map((field) => (
                <Form.Item
                    key={field.type}
                    label={<span style={{ fontSize: '1rem', fontWeight: 'normal' }}>{field.label}</span>}
                >
                    <Upload
                        name="avatar"
                        listType="picture-card"
                        className="avatar-uploader"
                        accept=".png,.jpg,.jpeg,.pdf"
                        disabled={field.status === 1 || field.status === 0}
                        showUploadList={false}
                        beforeUpload={(file) => handleFileUpload(file, field.type)}
                    >
                        {field.status === 1 ? (
                            <Button type="primary" icon={<CheckCircleOutlined />} size='small' style={{ fontFamily: 'Calibri, sans-serif', background: '#10B981', borderColor: '#10B981', color: 'white', cursor: 'default' }}>
                                Verified
                            </Button>
                        ) : field.status === 0 ? (
                            <Button type="primary" icon={<InfoCircleOutlined />} size='small' style={{ fontFamily: 'Calibri, sans-serif', background: '#AD1457', borderColor: '#AD1457', color: 'white', cursor: 'default' }}>
                                Pending
                            </Button>
                        ) : field.status === 3 ? (
                            <Popover content={<p style={{ fontSize: '0.75rem', margin: 0 }}>Your document was rejected. Please upload a valid document.</p>} title="Document Rejected" trigger="hover">
                                <Button type="primary" icon={<CloseCircleOutlined />} size='small' style={{ fontFamily: 'Calibri, sans-serif', background: '#EF4444', borderColor: '#EF4444', color: 'white' }}>
                                    Rejected
                                </Button>
                            </Popover>
                        ) : (
                            uploadButton
                        )}
                    </Upload>
                </Form.Item>
            ))}
        </Form>
    );
};

export default Verifications;
